import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import {
  Rocket,
  UserPlus,
  Trophy,
  Target,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Gift,
  Clock,
  Eye,
  EyeOff,
  Copy,
  ArrowRight,
  Sparkles,
  Building2,
  Users,
  Settings,
  X
} from 'lucide-react';

const SetupWizard = ({ companyName, onComplete, onSkip, isManualOpen = false }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Agent creation state
  const [agentForm, setAgentForm] = useState({
    prenom: '',
    nom: '',
    email: '',
    password: '',
    telephone: ''
  });
  const [createdAgent, setCreatedAgent] = useState(null);
  
  // Gamification state
  const [gamificationSettings, setGamificationSettings] = useState({
    points_appel: 10,
    points_email: 5,
    points_rdv: 50,
    points_signature: 200
  });

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setAgentForm({ ...agentForm, password });
  };

  const handleCreateAgent = async () => {
    if (!agentForm.prenom || !agentForm.nom || !agentForm.email || !agentForm.password) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.createAgent({
        prenom: agentForm.prenom,
        nom: agentForm.nom,
        email: agentForm.email,
        password: agentForm.password,
        telephone: agentForm.telephone
      });
      
      setCreatedAgent({
        ...response.data,
        password: agentForm.password // Keep password for display
      });
      
      toast.success('Agent créé avec succès !');
      setCurrentStep(1); // Go directly to gamification (was step 2, now step 1)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGamification = async () => {
    setLoading(true);
    try {
      // Try to get existing settings first
      const settingsRes = await api.getGamificationSettings();
      if (settingsRes.data && settingsRes.data.id) {
        // Update existing
        await api.updateGamificationSettings(settingsRes.data.id, {
          ...settingsRes.data,
          action_points: {
            ...settingsRes.data.action_points,
            call: gamificationSettings.points_appel,
            email: gamificationSettings.points_email,
            rdv: gamificationSettings.points_rdv,
            signature: gamificationSettings.points_signature
          }
        });
      } else {
        // Create new settings
        await api.createGamificationSettings({
          nom: 'Paramètres par défaut',
          action_points: {
            call: gamificationSettings.points_appel,
            email: gamificationSettings.points_email,
            rdv: gamificationSettings.points_rdv,
            signature: gamificationSettings.points_signature
          },
          is_active: true
        });
      }
      toast.success('Gamification configurée !');
    } catch (error) {
      // Continue even if error (settings might not be available yet)
      console.log('Gamification settings skipped:', error);
    }
    setCurrentStep(2); // Go to Test Agent (skip CEE)
    setLoading(false);
  };

  const handleTestAgent = () => {
    // Store agent credentials for quick switch
    localStorage.setItem('demo_agent_email', createdAgent?.email);
    localStorage.setItem('demo_agent_password', createdAgent?.password);
    setCurrentStep(3); // Go to Complete
  };

  const handleFinish = async () => {
    try {
      await api.updateMyCompany({ onboarding_completed: true });
    } catch (e) {
      // Silently fail
    }
    onComplete();
  };

  const copyCredentials = () => {
    const text = `Email: ${createdAgent?.email}\nMot de passe: ${createdAgent?.password}`;
    navigator.clipboard.writeText(text);
    toast.success('Identifiants copiés !');
  };

  const steps = [
    {
      icon: UserPlus,
      title: "Créer votre premier agent",
      subtitle: "Utilisez votre licence gratuite",
      color: "blue"
    },
    {
      icon: Trophy,
      title: "Configurer la gamification",
      subtitle: "Motivez vos agents",
      color: "amber"
    },
    {
      icon: Target,
      title: "Tester le portail agent",
      subtitle: "Découvrez l'expérience agent",
      color: "rose"
    },
    {
      icon: CheckCircle,
      title: "Configuration terminée",
      subtitle: "Votre espace est prêt",
      color: "green"
    }
  ];

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;
  const colorClasses = {
    blue: { bg: 'bg-blue-100', icon: 'text-blue-600', progress: 'bg-blue-500' },
    emerald: { bg: 'bg-emerald-100', icon: 'text-emerald-600', progress: 'bg-emerald-500' },
    amber: { bg: 'bg-amber-100', icon: 'text-amber-600', progress: 'bg-amber-500' },
    purple: { bg: 'bg-purple-100', icon: 'text-purple-600', progress: 'bg-purple-500' },
    rose: { bg: 'bg-rose-100', icon: 'text-rose-600', progress: 'bg-rose-500' },
    green: { bg: 'bg-green-100', icon: 'text-green-600', progress: 'bg-green-500' }
  };
  const colors = colorClasses[currentStepData.color];

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/95 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      {/* Close button (X) - top right */}
      <button
        onClick={onSkip}
        data-testid="wizard-close-button"
        className="fixed top-4 right-4 z-[10000] w-10 h-10 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-full flex items-center justify-center transition-all shadow-lg border border-slate-700"
        title="Fermer le guide"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="w-full max-w-2xl my-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600/20 rounded-full mb-4">
            <Gift className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-medium">
              {isManualOpen ? 'Guide de configuration' : 'Essai gratuit • 7 jours • 1 licence offerte'}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-white">
            Configuration de {companyName || 'votre espace'}
          </h1>
        </div>

        {/* Progress Bar */}
        <div className="flex gap-1 mb-6">
          {steps.map((step, i) => (
            <div
              key={step.title}
              className={`h-2 flex-1 rounded-full transition-all ${
                i < currentStep ? 'bg-emerald-500' :
                i === currentStep ? colors.progress : 'bg-slate-700'
              }`}
            />
          ))}
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${colors.icon}`} />
          </div>
          <div>
            <p className="text-white font-bold">{currentStepData.title}</p>
            <p className="text-slate-400 text-sm">{currentStepData.subtitle}</p>
          </div>
          <span className="text-slate-500 text-sm ml-auto">
            {currentStep + 1} / {steps.length}
          </span>
        </div>

        {/* Step Content */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            {/* Step 0: Create Agent */}
            {currentStep === 0 && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-900/30 border border-blue-700/50 rounded-lg">
                  <p className="text-blue-300 text-sm">
                    <Clock className="w-4 h-4 inline mr-2" />
                    Votre licence gratuite vous permet de créer 1 agent pour tester la plateforme.
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Prénom *</Label>
                    <Input
                      value={agentForm.prenom}
                      onChange={(e) => setAgentForm({...agentForm, prenom: e.target.value})}
                      placeholder="Pierre"
                      className="mt-1 bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Nom *</Label>
                    <Input
                      value={agentForm.nom}
                      onChange={(e) => setAgentForm({...agentForm, nom: e.target.value})}
                      placeholder="Martin"
                      className="mt-1 bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-300">Email *</Label>
                  <Input
                    type="email"
                    value={agentForm.email}
                    onChange={(e) => setAgentForm({...agentForm, email: e.target.value})}
                    placeholder="pierre.martin@votreentreprise.fr"
                    className="mt-1 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Mot de passe *</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="relative flex-1">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={agentForm.password}
                        onChange={(e) => setAgentForm({...agentForm, password: e.target.value})}
                        placeholder="Min. 6 caractères"
                        className="bg-slate-700 border-slate-600 text-white pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <Button
                      variant="outline"
                      onClick={generatePassword}
                      className="border-slate-600 text-slate-300"
                    >
                      Générer
                    </Button>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-300">Téléphone (optionnel)</Label>
                  <Input
                    value={agentForm.telephone}
                    onChange={(e) => setAgentForm({...agentForm, telephone: e.target.value})}
                    placeholder="06 12 34 56 78"
                    className="mt-1 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
            )}

            {/* Step 1: Gamification (was Step 2) */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="p-4 bg-amber-900/30 border border-amber-700/50 rounded-lg">
                  <p className="text-amber-300 text-sm">
                    <Trophy className="w-4 h-4 inline mr-2" />
                    Définissez les points gagnés pour chaque action. Cela motive vos agents !
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <Label className="text-slate-300">Appel passé</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        value={gamificationSettings.points_appel}
                        onChange={(e) => setGamificationSettings({...gamificationSettings, points_appel: parseInt(e.target.value) || 0})}
                        className="bg-slate-700 border-slate-600 text-white w-20"
                      />
                      <span className="text-slate-400">points</span>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <Label className="text-slate-300">Email envoyé</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        value={gamificationSettings.points_email}
                        onChange={(e) => setGamificationSettings({...gamificationSettings, points_email: parseInt(e.target.value) || 0})}
                        className="bg-slate-700 border-slate-600 text-white w-20"
                      />
                      <span className="text-slate-400">points</span>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <Label className="text-slate-300">RDV obtenu</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        value={gamificationSettings.points_rdv}
                        onChange={(e) => setGamificationSettings({...gamificationSettings, points_rdv: parseInt(e.target.value) || 0})}
                        className="bg-slate-700 border-slate-600 text-white w-20"
                      />
                      <span className="text-slate-400">points</span>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <Label className="text-slate-300">Affaire signée</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        value={gamificationSettings.points_signature}
                        onChange={(e) => setGamificationSettings({...gamificationSettings, points_signature: parseInt(e.target.value) || 0})}
                        className="bg-slate-700 border-slate-600 text-white w-20"
                      />
                      <span className="text-slate-400">points</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-3 bg-slate-700/30 rounded-lg">
                  <p className="text-slate-400 text-sm text-center">
                    🏆 Niveaux : Bronze (100 pts) → Argent (500 pts) → Or (1000 pts) → Diamant (2000 pts)
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Test Agent Portal */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="p-4 bg-rose-900/30 border border-rose-700/50 rounded-lg">
                  <p className="text-rose-300 text-sm">
                    <Target className="w-4 h-4 inline mr-2" />
                    Votre agent {createdAgent?.prenom} {createdAgent?.nom} est prêt !
                  </p>
                </div>
                
                <div className="p-6 bg-slate-700/50 rounded-lg text-center">
                  <div className="w-16 h-16 bg-emerald-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-white font-bold text-lg">
                    {createdAgent?.prenom} {createdAgent?.nom}
                  </h3>
                  <p className="text-slate-400">{createdAgent?.email}</p>
                  
                  <div className="mt-4 p-4 bg-slate-800 rounded-lg text-left">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-400 text-sm">Email :</span>
                      <span className="text-white font-mono text-sm">{createdAgent?.email}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Mot de passe :</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-mono text-sm">
                          {showPassword ? createdAgent?.password : '••••••••••'}
                        </span>
                        <button onClick={() => setShowPassword(!showPassword)} className="text-slate-400 hover:text-white">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={copyCredentials}
                    className="mt-4 border-slate-600 text-slate-300"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copier les identifiants
                  </Button>
                </div>
                
                <p className="text-slate-500 text-sm text-center">
                  Vous pourrez vous connecter avec ces identifiants pour tester le portail agent
                </p>
              </div>
            )}

            {/* Step 3: Complete */}
            {currentStep === 3 && (
              <div className="space-y-4 text-center">
                <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center mx-auto">
                  <Sparkles className="w-10 h-10 text-green-400" />
                </div>
                
                <h2 className="text-2xl font-bold text-white">
                  Votre espace est prêt !
                </h2>
                
                <p className="text-slate-400">
                  Vous avez configuré avec succès :
                </p>
                
                <div className="grid grid-cols-2 gap-3 text-left max-w-md mx-auto">
                  <div className="flex items-center gap-2 text-emerald-400">
                    <CheckCircle className="w-5 h-5" />
                    <span>1 agent créé</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-400">
                    <CheckCircle className="w-5 h-5" />
                    <span>Gamification active</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-400">
                    <CheckCircle className="w-5 h-5" />
                    <span>Paramètres CEE</span>
                  </div>
                </div>
                
                <div className="p-4 bg-emerald-900/30 border border-emerald-700/50 rounded-lg mt-6">
                  <p className="text-emerald-300">
                    <Gift className="w-4 h-4 inline mr-2" />
                    Vous avez 7 jours pour tester toutes les fonctionnalités gratuitement !
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer Actions */}
        <div className="flex items-center justify-between mt-6">
          <div>
            {currentStep > 0 && currentStep < 3 ? (
              <Button
                variant="ghost"
                onClick={() => setCurrentStep(currentStep - 1)}
                className="text-slate-400 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédent
              </Button>
            ) : currentStep === 0 ? (
              <Button
                variant="ghost"
                onClick={onSkip}
                className="text-slate-500 hover:text-slate-300"
              >
                Passer la configuration
              </Button>
            ) : null}
          </div>

          <div>
            {currentStep === 0 && (
              <Button
                onClick={handleCreateAgent}
                disabled={loading || !agentForm.prenom || !agentForm.email || !agentForm.password}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? 'Création...' : 'Créer l\'agent'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            
            {currentStep === 1 && (
              <Button
                onClick={handleSaveGamification}
                disabled={loading}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {loading ? 'Enregistrement...' : 'Continuer'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            
            {currentStep === 2 && (
              <Button
                onClick={handleTestAgent}
                className="bg-rose-600 hover:bg-rose-700"
              >
                Continuer
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            
            {currentStep === 3 && (
              <Button
                onClick={handleFinish}
                className="bg-green-600 hover:bg-green-700"
              >
                Accéder au tableau de bord
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
